// Hook pour détecter la vitesse de connexion
import { useState, useEffect } from 'react';

export type NetworkSpeed = 'slow' | 'medium' | 'fast';

/**
 * Détecte la vitesse de connexion et recommande une qualité d'image en pourcentage
 */
export function useNetworkSpeed(): {
    speed: NetworkSpeed;
    recommendedQuality: number; // 0-95% pour l'API
    recommendedWidth: number;   // Largeur en pixels pour téléchargement
    isDetecting: boolean;
} {
    const [speed, setSpeed] = useState<NetworkSpeed>('medium');
    const [isDetecting, setIsDetecting] = useState(true);

    useEffect(() => {
        const detectSpeed = () => {
            // 1. Utiliser l'API Network Information si disponible
            const connection = (navigator as any).connection 
                || (navigator as any).mozConnection 
                || (navigator as any).webkitConnection;

            if (connection) {
                const effectiveType = connection.effectiveType;
                
                // Mapper effectiveType vers notre système
                if (effectiveType === 'slow-2g' || effectiveType === '2g') {
                    setSpeed('slow');
                } else if (effectiveType === '3g') {
                    setSpeed('medium');
                } else {
                    // 4g, 5g, wifi
                    setSpeed('fast');
                }
                
                console.log(`📶 Connexion détectée: ${effectiveType} → ${speed}`);
                setIsDetecting(false);
                return;
            }

            // 2. Fallback : Test de vitesse simple avec une petite image
            const testImage = new Image();
            const startTime = Date.now();
            const testSize = 50000; // 50KB de test
            
            testImage.onload = () => {
                const duration = Date.now() - startTime;
                const speedKbps = (testSize * 8) / duration; // Kbps
                
                if (speedKbps < 500) {
                    setSpeed('slow');
                } else if (speedKbps < 2000) {
                    setSpeed('medium');
                } else {
                    setSpeed('fast');
                }
                
                console.log(`📶 Vitesse mesurée: ${speedKbps.toFixed(0)} Kbps → ${speed}`);
                setIsDetecting(false);
            };

            testImage.onerror = () => {
                // En cas d'erreur, utiliser medium par défaut
                setSpeed('medium');
                setIsDetecting(false);
                console.log('📶 Détection échouée, utilisation de "medium" par défaut');
            };

            // Utiliser une image de test (1x1 pixel transparent base64)
            testImage.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
        };

        detectSpeed();
    }, []);

    // Mapper speed vers pourcentage de qualité (0-95) ET largeur recommandée
    const recommendedQuality: number = 
        speed === 'slow' ? 50 :      // 50% pour connexion lente
        speed === 'medium' ? 75 :    // 75% pour connexion moyenne
        85;                          // 85% pour connexion rapide
    
    const recommendedWidth: number = 
        speed === 'slow' ? 400 :     // 400px pour connexion lente (~50KB à 50%)
        speed === 'medium' ? 800 :   // 800px pour connexion moyenne (~150KB à 75%)
        1920;                        // 1920px pour connexion rapide (~500KB à 85%)

    return { speed, recommendedQuality, recommendedWidth, isDetecting };
}

